import { useMemo } from 'react'
import { toast } from 'sonner'

import { useMoveProject } from '@/features/projects/reorder'
import { PageHeader, SaveIndicator } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { SortableList } from '@/shared/components/dnd'
import { useT } from '@/shared/i18n'
import type { ProjectSummary } from '@/shared/types/domain'

import { usePriorityProjects } from './api'
import { ONGOING_REJECT_KEY, OngoingSection } from './components/OngoingSection'
import { PriorityRow } from './components/PriorityRow'

/**
 * `/priorities` — el orden global de qué se desarrolla primero.
 *
 * Una sola lista, cross-company (plan 7.2): priorizar por empresa y después
 * decidir a mano entre empresas es exactamente el problema que esto resuelve.
 */
export function PrioritiesPage() {
  const t = useT()
  const query = usePriorityProjects()
  const move = useMoveProject()

  const { ranked, ongoing } = useMemo(() => {
    const projects: ProjectSummary[] = query.data ?? []
    return {
      ranked: projects.filter((project) => !project.is_ongoing),
      ongoing: projects.filter((project) => project.is_ongoing),
    }
  }, [query.data])

  return (
    <div className="flex min-h-dvh flex-col">
      <PageHeader
        title={t('priorities.title')}
        description={t('priorities.description')}
        actions={<SaveIndicator />}
      />

      <div className="flex-1 px-4 py-3">
        {query.isPending ? (
          <SkeletonRows rows={8} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : ranked.length === 0 && ongoing.length === 0 ? (
          <EmptyState
            title={t('priorities.empty.title')}
            description={t('priorities.empty.description')}
          />
        ) : (
          <SortableList
            items={ranked}
            ariaLabel={t('priorities.ariaLabel')}
            className="hairline overflow-hidden rounded-[var(--radius-card)]"
            getLabel={(project) => project.name}
            onReorder={({ itemId, beforeId, afterId }) =>
              move.mutate({ id: itemId, beforeId, afterId })
            }
            onRejectedDrop={() => toast.info(t(ONGOING_REJECT_KEY))}
            renderItem={(project, state) => (
              <PriorityRow
                project={project}
                // El número sale del índice, no del server: después de un drop
                // optimista el `position` que vino del backend ya es viejo.
                position={state.index + 1}
                handle={state.handle}
                isDragging={state.isDragging}
              />
            )}
          >
            <OngoingSection projects={ongoing} index={ranked.length} />
          </SortableList>
        )}
      </div>
    </div>
  )
}
